import type { CompetitionDetail, CompetitionListItem, CompetitionStatus, CompetitionType } from "../types/database";
import { supabase } from "./supabase";

type RawCompetitionRow = {
  id: string;
  company_id: string;
  name: string;
  brief: string | null;
  deliverable_description: string | null;
  status: CompetitionStatus;
  opens_at: string | null;
  closes_at: string | null;
  max_team_size: number;
};

type RawCompetitionDetailRow = RawCompetitionRow & {
  company_id: string;
  engagement_model?: string | null;
  team_size_min?: number | null;
  competition_type?: CompetitionType | null;
  max_enrollment?: number | null;
  eligibility_schools?: string[] | null;
  eligibility_majors?: string[] | null;
  eligibility_grad_year_min?: number | null;
  eligibility_grad_year_max?: number | null;
};

function normalizeEngagementModel(v: unknown): CompetitionDetail["engagement_model"] {
  if (v === "licensing" || v === "rm" || v === "hybrid") return v;
  return "licensing";
}

function mapListRow(row: RawCompetitionRow, companyName: string): CompetitionListItem {
  return {
    id: row.id,
    name: row.name,
    brief: row.brief,
    deliverable_description: row.deliverable_description,
    status: row.status,
    closes_at: row.closes_at,
    company_name: companyName,
  };
}

function normalizeCompetitionType(v: unknown): CompetitionType {
  if (v === "uncapped" || v === "invite_only" || v === "capped") return v;
  return "capped";
}

function mapDetailRow(row: RawCompetitionDetailRow, companyName: string): CompetitionDetail {
  const base = mapListRow(row, companyName);
  const max = row.max_team_size;
  const min = row.team_size_min ?? max;
  return {
    ...base,
    company_id: row.company_id,
    engagement_model: normalizeEngagementModel(row.engagement_model),
    opens_at: row.opens_at,
    team_size_min: typeof min === "number" && !Number.isNaN(min) ? min : max,
    max_team_size: max,
    competition_type: normalizeCompetitionType(row.competition_type),
    max_enrollment: row.max_enrollment ?? null,
    eligibility_schools: row.eligibility_schools ?? null,
    eligibility_majors: row.eligibility_majors ?? null,
    eligibility_grad_year_min: row.eligibility_grad_year_min ?? null,
    eligibility_grad_year_max: row.eligibility_grad_year_max ?? null,
  };
}

const competitionListSelect =
  "id, company_id, name, brief, deliverable_description, status, opens_at, closes_at, max_team_size";

const competitionDetailSelect = [
  "id",
  "company_id",
  "engagement_model",
  "name",
  "brief",
  "deliverable_description",
  "status",
  "opens_at",
  "closes_at",
  "team_size_min",
  "max_team_size",
  "competition_type",
  "max_enrollment",
  "eligibility_schools",
  "eligibility_majors",
  "eligibility_grad_year_min",
  "eligibility_grad_year_max",
].join(", ");

export async function listOpenCompetitions(): Promise<CompetitionListItem[]> {
  const { data, error } = await supabase
    .from("competitions")
    .select(competitionListSelect)
    .in("status", ["open", "judging", "closed"])
    .order("closes_at", { ascending: true, nullsFirst: false });

  if (error || !data?.length) return [];

  const companyIds = Array.from(
    new Set((data as RawCompetitionRow[]).map((r) => r.company_id).filter(Boolean))
  );
  const { data: companies } = await supabase.from("companies").select("id, name").in("id", companyIds);

  const nameById = new Map<string, string>();
  for (const c of companies ?? []) {
    if (typeof c.name === "string" && c.name.trim()) nameById.set(c.id, c.name);
  }

  const out: CompetitionListItem[] = [];
  for (const row of data as RawCompetitionRow[]) {
    const companyName = nameById.get(row.company_id);
    if (!companyName) continue;
    out.push(mapListRow(row, companyName));
  }
  return out;
}

export async function getCompetition(id: string): Promise<CompetitionDetail | null> {
  const { data, error } = await supabase
    .from("competitions")
    .select(competitionDetailSelect)
    .eq("id", id)
    .maybeSingle();

  if (error || !data) return null;
  const row = data as unknown as RawCompetitionDetailRow;

  const { data: companyData } = await supabase
    .from("companies")
    .select("name")
    .eq("id", row.company_id)
    .maybeSingle();
  const companyName = companyData?.name;
  if (typeof companyName !== "string" || !companyName.trim()) return null;

  return mapDetailRow(row, companyName);
}
